import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import {
  Save as SaveIcon,
  LocationOn as LocationIcon,
  AccessTime as TimeIcon,
  Event as EventIcon,
  People as PeopleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as DateIcon
} from '@mui/icons-material';

import api from '../utils/api';
import useThemeStyles from '../theme/useThemeStyles';

function WeddingVenues({ embedded, highlightField }) {
  const styles = useThemeStyles();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({
    ceremony: {
      business: '',
      name: '',
      address: '',
      date: '',
      time: '',
      capacity: '',
      contact: {
        name: '',
        phone: '',
        email: ''
      },
      notes: ''
    },
    reception: {
      business: '',
      name: '',
      address: '',
      date: '',
      time: '',
      capacity: '',
      eveningCapacity: '',
      contact: {
        name: '',
        phone: '',
        email: ''
      },
      notes: '',
      sameAsCeremony: false
    }
  });

  const [shortlistedSuppliers, setShortlistedSuppliers] = useState([]);
  const [selectedCeremonySupplierId, setSelectedCeremonySupplierId] = useState('');
  const [selectedReceptionSupplierId, setSelectedReceptionSupplierId] = useState('');

  const ceremonyCapacityRef = useRef(null);
  const receptionCapacityRef = useRef(null);
  const eveningCapacityRef = useRef(null);
  const [activeHighlight, setActiveHighlight] = useState(null);

  useEffect(() => {
    if (!highlightField || loading) return;

    let targetRef = null;
    if (highlightField === 'ceremonyCapacity') targetRef = ceremonyCapacityRef.current;
    if (highlightField === 'receptionCapacity') targetRef = receptionCapacityRef.current;
    if (highlightField === 'eveningCapacity') targetRef = eveningCapacityRef.current;

    if (targetRef && targetRef.scrollIntoView) {
      // Small delay to ensure layout is ready
      setTimeout(() => {
        try {
          targetRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
          targetRef.focus?.();
          setActiveHighlight(highlightField);
        } catch (e) {}
      }, 150);
    }

    const timeout = setTimeout(() => setActiveHighlight(null), 2500);
    return () => clearTimeout(timeout);
  }, [highlightField, loading]);

  useEffect(() => {
    fetchVenueDetails();
    fetchShortlistedSuppliers();
  }, []);

  // Infer supplier selections from saved venue details so the relationship is preserved after reload
  useEffect(() => {
    if (shortlistedSuppliers.length === 0) return;

    // Ceremony from stored relationship
    if (!selectedCeremonySupplierId && formData.ceremony.business) {
      setSelectedCeremonySupplierId(formData.ceremony.business);
    }

    // Ceremony
    if (!selectedCeremonySupplierId && (formData.ceremony.name || formData.ceremony.address)) {
      const match = shortlistedSuppliers.find(
        (s) =>
          s.supplier &&
          ((s.supplier.businessName && s.supplier.businessName === formData.ceremony.name) ||
            (s.supplier.address && s.supplier.address === formData.ceremony.address))
      );
      if (match?.supplier?._id) {
        setSelectedCeremonySupplierId(match.supplier._id);
      }
    }

    // Reception from stored relationship
    if (!selectedReceptionSupplierId && formData.reception.business) {
      setSelectedReceptionSupplierId(formData.reception.business);
    }

    // Reception
    if (!selectedReceptionSupplierId && (formData.reception.name || formData.reception.address)) {
      const match = shortlistedSuppliers.find(
        (s) =>
          s.supplier &&
          ((s.supplier.businessName && s.supplier.businessName === formData.reception.name) ||
            (s.supplier.address && s.supplier.address === formData.reception.address))
      );
      if (match?.supplier?._id) {
        setSelectedReceptionSupplierId(match.supplier._id);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- verified intentional deps
  }, [
    shortlistedSuppliers,
    formData.ceremony.name,
    formData.ceremony.address,
    formData.reception.name,
    formData.reception.address,
    selectedCeremonySupplierId,
    selectedReceptionSupplierId,
  ]);

  const fetchVenueDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/wedding-details/venues');
      if (response.data) {

        const formatDate = (date) => {
          if (!date) return '';
          if (date instanceof Date) return date.toISOString().split('T')[0]; // Date object
          if (typeof date === 'string') return date.split('T')[0]; // ISO string
          return '';
        };

        setFormData({
          ceremony: {
            business: response.data.ceremony?.business || '',
            name: response.data.ceremony?.name || '',
            address: response.data.ceremony?.address || '',
            date: formatDate(response.data.ceremony?.date),
            time: response.data.ceremony?.time || '',
            capacity: response.data.ceremony?.capacity || '',
            contact: {
              name: response.data.ceremony?.contact?.name || '',
              phone: response.data.ceremony?.contact?.phone || '',
              email: response.data.ceremony?.contact?.email || ''
            },
            notes: response.data.ceremony?.notes || ''
          },
          reception: {
            business: response.data.reception?.business || '',
            name: response.data.reception?.name || '',
            address: response.data.reception?.address || '',
            date: formatDate(response.data.reception?.date),
            time: response.data.reception?.time || '',
            capacity: response.data.reception?.capacity || '',
            eveningCapacity: response.data.reception?.eveningCapacity ?? '',
            contact: {
              name: response.data.reception?.contact?.name || '',
              phone: response.data.reception?.contact?.phone || '',
              email: response.data.reception?.contact?.email || ''
            },
            notes: response.data.reception?.notes || '',
            sameAsCeremony: response.data.reception?.sameAsCeremony || false
          }
        });
      }
    } catch (error) {
      setError('Failed to load venue details');
    } finally {
      setLoading(false);
    }
  };

  const fetchShortlistedSuppliers = async () => {
    try {
      const response = await api.get('/api/user-suppliers');
      const allSuppliers = response.data || [];
      const shortlisted = allSuppliers.filter(
        (s) =>
          s.status === 'active' &&
          s.supplier &&
          s.supplier._id &&
          s.supplier.category === 'venue'
      );
      setShortlistedSuppliers(shortlisted);
    } catch (error) {
      // Silently ignore supplier loading errors so venue details still work
    }
  };

  const handleChange = (venue, field, subfield = null) => (event) => {
    const value = event.target.value;
    setFormData(prev => {
      if (subfield) {
        return {
          ...prev,
          [venue]: {
            ...prev[venue],
            [field]: {
              ...prev[venue][field],
              [subfield]: value
            }
          }
        };
      }
      return {
        ...prev,
        [venue]: {
          ...prev[venue],
          [field]: value
        }
      };
    });
  };

  const handleSupplierPrefill = (venue, supplierId) => {
    if (venue === 'ceremony') {
      setSelectedCeremonySupplierId(supplierId);
    }
    if (venue === 'reception') {
      setSelectedReceptionSupplierId(supplierId);
    }

    if (!supplierId) {
      // Switch back to manual entry: clear prefilled venue fields and capacities
      setFormData((prev) => ({
        ...prev,
        [venue]: {
          ...prev[venue],
          business: '',
          name: '',
          address: '',
          capacity: '',
          ...(venue === 'reception' ? { eveningCapacity: '' } : {})
        }
      }));
      return;
    }

    const supplier = shortlistedSuppliers.find(
      (s) => s.supplier && String(s.supplier._id) === String(supplierId)
    )?.supplier;

    if (!supplier) return;

    const vd = supplier.venueDetails;
    const fallback = venue === 'ceremony' ? formData.ceremony.capacity : formData.reception.capacity;
    const capacity = venue === 'ceremony'
      ? (vd?.ceremonyCapacity != null ? vd.ceremonyCapacity : fallback)
      : (vd?.receptionCapacity != null ? vd.receptionCapacity : fallback);

    setFormData((prev) => {
      const next = { ...prev, [venue]: { ...prev[venue], business: supplier._id, name: supplier.businessName || prev[venue].name, address: supplier.address || prev[venue].address, capacity: capacity !== undefined && capacity !== null ? capacity : prev[venue].capacity } };
      if (venue === 'reception') {
        next.reception.eveningCapacity = vd?.eveningCapacity != null ? vd.eveningCapacity : prev.reception.eveningCapacity ?? '';
      }
      return next;
    });
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      // Ensure any linked suppliers are reflected in the payload before saving
      const payload = { ...formData };

      const findSupplierById = (id) =>
        shortlistedSuppliers.find((s) => s.supplier && s.supplier._id === id)?.supplier;

      if (selectedCeremonySupplierId) {
        const supplier = findSupplierById(selectedCeremonySupplierId);
        if (supplier) {
          payload.ceremony = {
            ...payload.ceremony,
            business: supplier._id,
            name: supplier.businessName || payload.ceremony.name,
            address: supplier.address || payload.ceremony.address,
            capacity: supplier.venueDetails?.ceremonyCapacity ?? payload.ceremony.capacity,
          };
        }
      } else {
        // Manual mode – clear relationship
        payload.ceremony = {
          ...payload.ceremony,
          business: undefined,
        };
      }

      if (selectedReceptionSupplierId) {
        const supplier = findSupplierById(selectedReceptionSupplierId);
        if (supplier) {
          payload.reception = {
            ...payload.reception,
            business: supplier._id,
            name: supplier.businessName || payload.reception.name,
            address: supplier.address || payload.reception.address,
            capacity: supplier.venueDetails?.receptionCapacity ?? payload.reception.capacity,
            eveningCapacity: supplier.venueDetails?.eveningCapacity ?? payload.reception.eveningCapacity,
          };
        }
      } else {
        payload.reception = {
          ...payload.reception,
          business: undefined,
        };
      }

      await api.put('/api/wedding-details/venues', payload);
      setSuccess('Venue details saved successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError('Failed to save venue details');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={styles.patterns.loadingState}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: embedded ? 0 : undefined, ...(embedded ? {} : styles.container.page) }}>
      <Typography component="h2" className="pagename" sx={{ px: 2, ...(embedded ? {} : styles.text.pagename) }}>
        Venues & Dates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, px: 2 }}>
        Manage your ceremony and reception venue details
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      <Grid container spacing={embedded ? 2 : 3}>
        {/* Ceremony Venue */}
        <Grid item xs={12}>
          <Card sx={styles.card.default}>
            <CardContent sx={embedded ? { py: 1.5, px: 2, '&:last-child': { pb: 1.5 } } : styles.spacing.card}>
              <Box sx={{ ...styles.layout.flexRow, mb: 2, gap: 1 }}>
                <EventIcon color="primary" />
                <Typography variant="h6" sx={{...styles.text.sectionTitle, mb: 0}}>
                  Ceremony Venue
                </Typography>
              </Box>
              
              <Box sx={styles.patterns.formContainer}>
                {shortlistedSuppliers.length > 0 && (
                  <TextField
                    select
                    fullWidth
                    label="Use address from your venue suppliers"
                    value={selectedCeremonySupplierId}
                    onChange={(e) => handleSupplierPrefill('ceremony', e.target.value)}
                    helperText="Choose one of Your Suppliers to prefill the venue name and address, or select as 'Type address manually' to type it yourself."
                    InputLabelProps={{ shrink: true }}
                    SelectProps={{
                      displayEmpty: true,
                      renderValue: (value) => {
                        if (!value) {
                          return 'Type address manually';
                        }
                        const match = shortlistedSuppliers.find(
                          (us) => us.supplier && us.supplier._id === value
                        );
                        return match?.supplier?.businessName || 'Selected supplier';
                      },
                    }}
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="">
                      Type address manually
                    </MenuItem>
                    {shortlistedSuppliers.map((us) => (
                      <MenuItem key={us._id} value={us.supplier._id}>
                        {us.supplier.businessName || 'Unnamed supplier'}
                      </MenuItem>
                    ))}
                  </TextField>
                )}

                {(() => {
                  const selected = shortlistedSuppliers.find(
                    (s) => s.supplier && s.supplier._id === selectedCeremonySupplierId
                  );
                  const supplier = selected?.supplier;
                  const nameValue = supplier?.businessName || formData.ceremony.name;
                  const addressValue = supplier?.address || formData.ceremony.address;
                  return (
                    <>
                      <TextField
                        fullWidth
                        label="Venue Name"
                        value={nameValue}
                        onChange={handleChange('ceremony', 'name')}
                        placeholder="e.g., St. Mary's Church"
                        disabled={Boolean(selectedCeremonySupplierId)}
                      />
                      
                      <TextField
                        fullWidth
                        label="Address"
                        multiline
                        rows={2}
                        value={addressValue}
                        onChange={handleChange('ceremony', 'address')}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <LocationIcon color="action" />
                            </InputAdornment>
                          ),
                        }}
                        disabled={Boolean(selectedCeremonySupplierId)}
                      />
                    </>
                  );
                })()}
                
                <Grid
                  container
                  rowSpacing={2}
                  columnSpacing={0}
                  sx={{ m: 0 }}
                  style={{ margin: 0 }}
                >
                  <Grid item xs={12} lg={6}>
                    <TextField
                      fullWidth
                      label="Date"
                      type="date"
                      value={formData.ceremony.date}
                      onChange={handleChange('ceremony', 'date')}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DateIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        min: new Date(new Date().setDate(new Date().getDate() + 1))
                          .toISOString()
                          .split('T')[0]
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <TextField
                      fullWidth
                      label="Time"
                      value={formData.ceremony.time}
                      onChange={handleChange('ceremony', 'time')}
                      placeholder="3:00 PM"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TimeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} xl={6}>
                    <TextField
                      fullWidth
                      label="Ceremony capacity"
                      type="number"
                      value={formData.ceremony.capacity}
                      onChange={handleChange('ceremony', 'capacity')}
                      disabled={Boolean(selectedCeremonySupplierId)}
                      helperText="Maximum number of guests for ceremony"
                      inputRef={ceremonyCapacityRef}
                      sx={activeHighlight === 'ceremonyCapacity' ? {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                          boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.3)',
                        },
                      } : undefined}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PeopleIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                <Box sx={{ ...styles.layout.flexRow, mt: 4, gap: 1 }}>
                  <PhoneIcon color="primary" />
                  <Typography variant="subtitle2" sx={{...styles.text.sectionTitle, mb: 0}}>
                    Contact Information
                  </Typography>
                </Box>
                
                <TextField
                  fullWidth
                  label="Contact Name"
                  value={formData.ceremony.contact.name}
                  onChange={handleChange('ceremony', 'contact', 'name')}
                />
                
                <Grid
                  container
                  rowSpacing={2}
                  columnSpacing={0}
                  sx={{ m: 0 }}
                  style={{ margin: 0 }}
                >
                  <Grid
                    item
                    xs={12}
                    lg={6}
                    sx={{ pr: { lg: 1, xs: 0 } }}
                  >
                    <TextField
                      fullWidth
                      label="Email"
                      type="email"
                      value={formData.ceremony.contact.email}
                      onChange={handleChange('ceremony', 'contact', 'email')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <EmailIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          py: 1.25,
                        },
                        mb: { xs: 1.5, lg: 0 },
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    lg={6}
                    sx={{ pl: { lg: 1, xs: 0 } }}
                  >
                    <TextField
                      fullWidth
                      label="Phone"
                      value={formData.ceremony.contact.phone}
                      onChange={handleChange('ceremony', 'contact', 'phone')}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PhoneIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        '& .MuiInputBase-root': {
                          py: 1.25,
                        },
                        mb: { xs: 1.5, lg: 0 },
                      }}
                    />
                  </Grid>
                </Grid>
                
                <TextField
                  fullWidth
                  label="Notes"
                  multiline
                  rows={3}
                  value={formData.ceremony.notes}
                  onChange={handleChange('ceremony', 'notes')}
                  placeholder="Special instructions, parking info, etc."
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Reception Venue */}
        <Grid item xs={12}>
          <Card sx={styles.card.default}>
            <CardContent sx={embedded ? { py: 1.5, px: 2, '&:last-child': { pb: 1.5 } } : styles.spacing.card}>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  mb: 2,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <LocationIcon color="primary" />
                  <Typography variant="h6" sx={{ mb: 0 }}>
                    Reception Venue
                  </Typography>
                </Box>

                <FormControlLabel
                  labelPlacement="start"
                  label="Same as Ceremony"
                  control={
                    <Switch
                      checked={formData.reception.sameAsCeremony}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          reception: {
                            ...prev.reception,
                            sameAsCeremony: e.target.checked,
                          },
                        }))
                      }
                    />
                  }
                />
              </Box>
              
              <Box sx={styles.patterns.formContainer}>
                {!formData.reception.sameAsCeremony && (
                  <>
                    {shortlistedSuppliers.length > 0 && (
                      <TextField
                        select
                        fullWidth
                        label="Use address from your venue suppliers"
                        value={selectedReceptionSupplierId}
                        onChange={(e) => handleSupplierPrefill('reception', e.target.value)}
                        helperText="Choose one of Your Suppliers to prefill the venue name and address, or select as 'Type address manually' to type it yourself."
                        InputLabelProps={{ shrink: true }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (value) => {
                            if (!value) {
                              return 'Type address manually';
                            }
                            const match = shortlistedSuppliers.find(
                              (us) => us.supplier && us.supplier._id === value
                            );
                            return match?.supplier?.businessName || 'Selected supplier';
                          },
                        }}
                        sx={{ mb: 2 }}
                      >
                        <MenuItem value="">
                          Type address manually
                        </MenuItem>
                        {shortlistedSuppliers.map((us) => (
                          <MenuItem key={us._id} value={us.supplier._id}>
                            {us.supplier.businessName || 'Unnamed supplier'}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}

                    {(() => {
                      const selected = shortlistedSuppliers.find(
                        (s) => s.supplier && s.supplier._id === selectedReceptionSupplierId
                      );
                      const supplier = selected?.supplier;
                      const nameValue = supplier?.businessName || formData.reception.name;
                      const addressValue = supplier?.address || formData.reception.address;
                      return (
                        <>
                          <TextField
                            fullWidth
                            label="Venue Name"
                            value={nameValue}
                            onChange={handleChange('reception', 'name')}
                            placeholder="e.g., Grand Ballroom"
                            disabled={Boolean(selectedReceptionSupplierId)}
                          />

                          <TextField
                            fullWidth
                            label="Address"
                            multiline
                            rows={2}
                            value={addressValue}
                            onChange={handleChange('reception', 'address')}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon color="action" />
                                </InputAdornment>
                              ),
                            }}
                            disabled={Boolean(selectedReceptionSupplierId)}
                          />
                        </>
                      );
                    })()}
                  </>
                )}

                <Grid
                  container
                  rowSpacing={2}
                  columnSpacing={0}
                  sx={{ m: 0 }}
                  style={{ margin: 0 }}
                >
                  <Grid item xs={12} lg={6}>
                    <TextField
                      fullWidth
                      label="Date"
                      type="date"
                      value={formData.reception.date}
                      onChange={handleChange('reception', 'date')}
                      InputLabelProps={{ shrink: true }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <DateIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                      inputProps={{
                        min: new Date(new Date().setDate(new Date().getDate() + 1))
                          .toISOString()
                          .split('T')[0]
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} lg={6}>
                    <TextField
                      fullWidth
                      label="Time"
                      value={formData.reception.time}
                      onChange={handleChange('reception', 'time')}
                      placeholder="6:00 PM"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <TimeIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} xl={6}>
                    <TextField
                      fullWidth
                      label="Reception capacity"
                      type="number"
                      value={formData.reception.capacity}
                      onChange={handleChange('reception', 'capacity')}
                      disabled={Boolean(selectedReceptionSupplierId)}
                      helperText="Maximum number of guests for reception"
                      inputRef={receptionCapacityRef}
                      sx={activeHighlight === 'receptionCapacity' ? {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                          boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.3)',
                        },
                      } : undefined}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PeopleIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} xl={6}>
                    <TextField
                      fullWidth
                      label="Evening capacity"
                      type="number"
                      value={formData.reception.eveningCapacity}
                      onChange={handleChange('reception', 'eveningCapacity')}
                      disabled={Boolean(selectedReceptionSupplierId)}
                      helperText="Maximum number of guests for evening"
                      inputRef={eveningCapacityRef}
                      sx={activeHighlight === 'eveningCapacity' ? {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: '#1976d2',
                          boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.3)',
                        },
                      } : undefined}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <PeopleIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>

                {!formData.reception.sameAsCeremony && (
                  <>
                    <Box sx={{ ...styles.layout.flexRow, mt: 4, gap: 1 }}>
                      <PhoneIcon color="primary" />
                      <Typography variant="subtitle2" sx={{...styles.text.sectionTitle, mb: 0}}>
                        Contact Information
                      </Typography>
                    </Box>
                    
                    <TextField
                      fullWidth
                      label="Contact Name"
                      value={formData.reception.contact.name}
                      onChange={handleChange('reception', 'contact', 'name')}
                    />
                    
                    <Grid
                      container
                      rowSpacing={2}
                      columnSpacing={0}
                      sx={{ m: 0 }}
                      style={{ margin: 0 }}
                    >
                      <Grid
                        item
                        xs={12}
                        lg={6}
                        sx={{ pr: { lg: 1, xs: 0 } }}
                      >
                        <TextField
                          fullWidth
                          label="Email"
                          type="email"
                          value={formData.reception.contact.email}
                          onChange={handleChange('reception', 'contact', 'email')}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <EmailIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              py: 1.25,
                            },
                            mb: { xs: 1.5, lg: 0 },
                          }}
                        />
                      </Grid>
                      <Grid
                        item
                        xs={12}
                        lg={6}
                        sx={{ pl: { lg: 1, xs: 0 } }}
                      >
                        <TextField
                          fullWidth
                          label="Phone"
                          value={formData.reception.contact.phone}
                          onChange={handleChange('reception', 'contact', 'phone')}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <PhoneIcon color="action" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiInputBase-root': {
                              py: 1.25,
                            },
                            mb: { xs: 1.5, lg: 0 },
                          }}
                        />
                      </Grid>
                    </Grid>
                    
                    <TextField
                      fullWidth
                      label="Notes"
                      multiline
                      rows={3}
                      value={formData.reception.notes}
                      onChange={handleChange('reception', 'notes')}
                      placeholder="Catering info, layout preferences, etc."
                    />
                  </>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ ...styles.layout.flexCenter, mt: 3 }}>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSubmit}
          disabled={saving}
          size="large"
        >
          {saving ? 'Saving...' : 'Save Venue Details'}
        </Button>
      </Box>
    </Box>
  );
}

export default WeddingVenues; 